/*
 * usePlannerDrafts
 *
 * Keeps MainComponent thin by owning planner-draft orchestration: derives
 * tree/map views of all drafts from the RouteStore (single source of truth),
 * maintains draft selection in the tree reducer (the selection's only home),
 * and exposes per-draft discard/save handlers for the tree menu.
 */

import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  buildNewVoyage,
  savePlannedRoute,
} from "@/services/shippingDbApi";
import {
  draftRouteIdFromSelectionId,
  draftSelectionId,
  isDraftSelectionId,
  useDraftRoutes,
  useRouteStore,
} from "@/features/route/state/RouteStore.jsx";
import {
  createDraftDiscardState,
  createRoutePatchState,
} from "@/features/route/state/RouteState.jsx";
import {
  draftDisplayName,
  selectRenderableRoute,
} from "@/features/route/state/RouteSelectors.jsx";
import { normalizeSketchRoute } from "@/features/route/state/RouteAdapters.jsx";
import {
  isManualDraft,
  manualRoutePoints,
} from "@/features/voyagePlanner/manualSketch";
import { treeSelectionActions } from "@/features/routeTree/state/VesselTreeState.jsx";

// "NANTES" + "frnte" → "Nantes (FRNTE)"; mixed-case names are kept as is.
const portLabel = (port) => {
  const name = String(port?.name ?? "").trim();
  if (!name) return "";
  const cased =
    name === name.toUpperCase()
      ? name.toLowerCase().replace(/(^|[\s-])\p{L}/gu, (m) => m.toUpperCase())
      : name;
  return port?.code ? `${cased} (${port.code})` : cased;
};

// "<Vessel> <From>-<To>", "<Vessel> to <To>", "<Vessel> from <From>"; no port → empty, the user names it.
export const suggestVoyageName = (draft) => {
  const ctx = draft?.draftContext ?? {};
  const from = portLabel(ctx.startPort);
  const to = portLabel(ctx.finishPort);
  const leg = from && to ? `${from}-${to}` : to ? `to ${to}` : from ? `from ${from}` : "";
  return leg ? [ctx.vesselName, leg].filter(Boolean).join(" ") : "";
};

const toVoyageId = (value) => {
  if (value == null) return null;
  const id = Number(value);
  return Number.isFinite(id) && id > 0 ? id : null;
};

// Changes on a new compute; lets us detect a fresh draft per store slot.
function signatureOf(draft) {
  const features = draft?.points?.geojson?.features ?? [];
  if (features.length === 0) return null;
  const coordsOf = (f) => f?.geometry?.coordinates ?? [];
  const first = coordsOf(features[0]);
  const last = coordsOf(features[features.length - 1]);
  return `${features.length}|${first[0]},${first[1]}|${last[0]},${last[1]}`;
}

// A computed manual draft drawn as the sketch of its user-set points.
function sketchViewOf(draft) {
  const routeId = draft.points.routeId;
  const view = selectRenderableRoute(
    normalizeSketchRoute(routeId, manualRoutePoints(draft), {
      vesselName: draft.points.vesselName,
      draftContext: draft.draftContext,
    }),
    routeId,
  );
  return {
    ...view,
    points: {
      ...view.points,
      // An edit through the view writes points.name back; keep a user-given name.
      name: draft.points.name,
      vesselId: draft.points.vesselId,
      voyageId: draft.points.voyageId,
    },
  };
}

export default function usePlannerDrafts({
  treeSelectedRouteIds,
  treeDispatch,
  addNotification,
  accessContext,
  setAccessContext,
  onPersistedRouteSaved,
  onVoyageCreated,
  selectedVoyageIds,
  switchVoyagePlannerActive,
  importPreviewOpen,
  plannerDraftSlot,
  plannerPanelOpen = true,
}) {
  const draftRoutes = useDraftRoutes();
  const { state: routeStoreState, replaceRoute, upsertEntities, archiveVoyageSiblings } = useRouteStore();
  const storeStateRef = useRef(routeStoreState);
  storeStateRef.current = routeStoreState;

  // Import previews share the draft store but none of the planner policy
  // (auto-select, one-per-vessel, invalidation) — they are transient until
  // the import dialog saves or cancels. Sketches (no times yet) stay out as well.
  const plannerDraftRoutes = useMemo(
    () =>
      draftRoutes.filter(
        (draft) => draft?.source !== "import" && !draft?.sketch,
      ),
    [draftRoutes],
  );

  // Tree rows; a vessel-less draft renders on the map but has no tree row.
  const treeDraftRoutes = useMemo(
    () =>
      plannerDraftRoutes
        .map((draft) => {
          const routeId = draft?.points?.routeId;
          const vesselId = Number(
            draft?.draftContext?.vesselId ?? draft?.points?.vesselId,
          );
          if (!Number.isFinite(vesselId)) return null;
          return {
            id: draftSelectionId(routeId),
            routeId,
            vesselId,
            voyageId: toVoyageId(
              draft?.draftContext?.voyageId ?? draft?.points?.voyageId,
            ),
            type: "planned",
            routeType: "planned",
            source: "draft",
            persisted: false,
            name: draftDisplayName(draft, plannerDraftRoutes),
            suggestedVoyageName: suggestVoyageName(draft),
            timestamp: draft?.points?.timestamp ?? null,
            summary: draft?.summary ?? null,
            rawResponse: draft?.rawResponse ?? null,
          };
        })
        .filter(Boolean),
    [plannerDraftRoutes],
  );

  // Auto-select only on a fresh compute (signature change) so the user can
  // deselect a draft; drop selections whose draft vanished.
  const knownSignaturesRef = useRef(new Map());
  useEffect(() => {
    const live = new Map();
    plannerDraftRoutes.forEach((draft) => {
      const sig = signatureOf(draft);
      if (sig != null) live.set(draft.points.routeId, sig);
    });

    const staleSelectionIds = (treeSelectedRouteIds ?? []).filter(
      (id) => isDraftSelectionId(id) && !live.has(draftRouteIdFromSelectionId(id)),
    );
    const freshRouteIds = [...live.entries()]
      .filter(([routeId, sig]) => knownSignaturesRef.current.get(routeId) !== sig)
      .map(([routeId]) => routeId);

    knownSignaturesRef.current = live;
    if (staleSelectionIds.length === 0 && freshRouteIds.length === 0) return;

    const selected = treeSelectedRouteIds ?? [];
    staleSelectionIds.forEach((selectionId) => {
      treeDispatch(treeSelectionActions.deselectRoute(selectionId));
    });
    freshRouteIds.forEach((routeId) => {
      const selectionId = draftSelectionId(routeId);
      // Toggle semantics: guard so a recompute can't deselect its own draft.
      if (selected.includes(selectionId)) return;
      // Parents co-select the draft's voyage (matches a tree click).
      const draft = plannerDraftRoutes.find(
        (d) => d?.points?.routeId === routeId,
      );
      const vesselId = Number(
        draft?.draftContext?.vesselId ?? draft?.points?.vesselId,
      );
      const voyageId = toVoyageId(
        draft?.draftContext?.voyageId ?? draft?.points?.voyageId,
      );
      treeDispatch(
        treeSelectionActions.toggleRouteSelected(
          Number.isFinite(vesselId) ? vesselId : null,
          voyageId,
          selectionId,
        ),
      );
    });
  }, [plannerDraftRoutes, treeSelectedRouteIds, treeDispatch]);

  const selectedDraftRouteIds = useMemo(
    () =>
      (treeSelectedRouteIds ?? [])
        .filter(isDraftSelectionId)
        .map(draftRouteIdFromSelectionId)
        .filter((id) => id != null),
    [treeSelectedRouteIds],
  );

  // On the map: selected + voyage matches (or planner active).
  // Import previews render exactly while their dialog is open — a recalc
  // landing after cancel must not leave a ghost preview.
  const mapDraftRoutes = useMemo(() => {
    const selectedVoyageId = toVoyageId(selectedVoyageIds?.[0]);
    return draftRoutes.filter((draft) => {
      if (draft?.source === "import") return importPreviewOpen === true;
      if (draft?.sketch) return false;
      const routeId = draft?.points?.routeId;
      if (!selectedDraftRouteIds.includes(routeId)) return false;
      if (switchVoyagePlannerActive) return true;
      const draftVoyageId = toVoyageId(
        draft?.draftContext?.voyageId ?? draft?.points?.voyageId,
      );
      return selectedVoyageId === draftVoyageId;
    })
      // Same label as the tree row, derived against all planner drafts.
      .map((draft) => ({ ...draft, displayName: draftDisplayName(draft, plannerDraftRoutes) }));
  }, [
    draftRoutes,
    plannerDraftRoutes,
    selectedDraftRouteIds,
    selectedVoyageIds,
    switchVoyagePlannerActive,
    importPreviewOpen,
  ]);

  // The sketch is a planner tool like the start/finish markers: only while the panel is open.
  const slotRouteId =
    plannerPanelOpen && plannerDraftSlot?.showSketch
      ? plannerDraftSlot.routeId
      : null;
  const sketchView = plannerDraftSlot?.sketchView === true;
  const pathfinderMapRoutes = useMemo(() => {
    if (!switchVoyagePlannerActive || slotRouteId == null) return mapDraftRoutes;
    const slotDraft = draftRoutes.find(
      (draft) => draft?.points?.routeId === slotRouteId,
    );
    if (slotDraft?.sketch) return [...mapDraftRoutes, slotDraft];
    if (!sketchView || !isManualDraft(slotDraft)) return mapDraftRoutes;
    return [
      ...mapDraftRoutes.filter((draft) => draft?.points?.routeId !== slotRouteId),
      sketchViewOf(slotDraft),
    ];
  }, [draftRoutes, mapDraftRoutes, slotRouteId, sketchView, switchVoyagePlannerActive]);

  // Toggle selection (highlight) from the tree row. Parents (when the row
  // has a voyage) co-select the voyage like a plan-route click.
  const toggleDraftSelected = useCallback(
    (routeId, parents = {}) => {
      const selectionId = draftSelectionId(routeId);
      const vesselId = Number(parents?.vesselId);
      const voyageId = Number(parents?.voyageId);
      treeDispatch(
        treeSelectionActions.toggleRouteSelected(
          Number.isFinite(vesselId) ? vesselId : null,
          Number.isFinite(voyageId) && voyageId > 0 ? voyageId : null,
          selectionId,
        ),
      );
    },
    [treeDispatch],
  );

  const deselectDraft = useCallback(
    (routeId) => {
      treeDispatch(
        treeSelectionActions.deselectRoute(draftSelectionId(routeId)),
      );
    },
    [treeDispatch],
  );

  const handleDiscardDraft = useCallback(
    (draft) => {
      const routeId = Number(draft?.routeId ?? draft);
      if (!Number.isFinite(routeId)) return;
      replaceRoute(routeId, createDraftDiscardState(routeId));
      deselectDraft(routeId);
      addNotification?.({
        message: "Discarded suggested route",
        type: "info",
      });
    },
    [replaceRoute, deselectDraft, addNotification],
  );

  // Draft-only rename: store patch, no backend — the route has no DB row yet.
  const handleRenameDraft = useCallback(
    (passedRoute, nextName) => {
      const routeId = passedRoute?.routeId;
      const name = String(nextName ?? "").trim();
      if (!Number.isFinite(routeId) || !name) return;
      upsertEntities(createRoutePatchState(routeId, { name }));
    },
    [upsertEntities],
  );

  const discardDraftsForVoyage = useCallback(
    (voyageId) => {
      const targetId = toVoyageId(voyageId);
      if (targetId == null) return;
      plannerDraftRoutes.forEach((draft) => {
        const draftVoyageId = toVoyageId(
          draft?.draftContext?.voyageId ?? draft?.points?.voyageId,
        );
        if (draftVoyageId !== targetId) return;
        const routeId = draft?.points?.routeId;
        replaceRoute(routeId, createDraftDiscardState(routeId));
        deselectDraft(routeId);
      });
    },
    [plannerDraftRoutes, replaceRoute, deselectDraft],
  );

  const handleSaveDraft = useCallback(
    // `passedRoute` is the tree row; the geometry lives in the store draft.
    async (passedRoute, options = {}) => {
      const routeStoreId = Number(passedRoute?.routeId);
      const draft = plannerDraftRoutes.find(
        (d) => Number(d?.points?.routeId) === routeStoreId,
      );
      const vesselId = Number(
        passedRoute?.vesselId ?? draft?.draftContext?.vesselId,
      );
      if (!draft || !Number.isFinite(vesselId)) {
        addNotification?.({
          message: "Cannot save suggested route: missing vessel.",
          type: "error",
        });
        return;
      }
      // The save dialog decides the target; without it the draft's own voyage does.
      const existingVoyageId =
        options?.saveTarget === "new"
          ? null
          : toVoyageId(
              options?.voyageId ?? passedRoute?.voyageId ?? draft?.draftContext?.voyageId,
            );

      const points = draft?.points?.geojson?.features ?? [];
      if (!points.length) {
        addNotification?.({
          message: "Cannot save suggested route: no waypoints available.",
          type: "error",
        });
        return;
      }

      const isNewVoyage = existingVoyageId == null;
      const requestedName = options?.routeName?.trim?.() ?? "";
      const draftName = passedRoute?.name?.trim?.() ?? "";
      const routeName =
        requestedName || draftName || draftDisplayName(draft, plannerDraftRoutes);

      // Keep all pathfinder props so the save can persist the blobs.
      const waypointPayload = points.map((feature) => ({
        ...(feature?.properties ?? {}),
        lon: Number(feature?.geometry?.coordinates?.[0]),
        lat: Number(feature?.geometry?.coordinates?.[1]),
        legType: feature?.properties?.legType ?? feature?.properties?.leg_type,
        timestamp: feature?.properties?.timestamp ?? feature?.properties?.datetime_utc,
        type: feature?.properties?.type ?? feature?.properties?.waypoint_type,
      }));

      const jsonRequest = draft?.jsonRequest ?? undefined;
      const summary = draft?.summary ?? undefined;
      const optimisedFor = draft?.optimisedFor ?? undefined;

      // Base speed is per-waypoint; fall back to the first waypoint's value.
      const baseSpeed = Number(
        draft?.points?.basespeed ?? points[0]?.properties?.basespeed ?? 0,
      );

      const newVoyage = isNewVoyage
        ? buildNewVoyage({
            name:
              options?.voyageName?.trim?.() ||
              requestedName ||
              `Voyage ${new Date().toISOString().slice(0, 16).replace("T", " ")}`,
            features: points,
            etd: waypointPayload[0]?.timestamp,
            eta: waypointPayload.at(-1)?.timestamp,
            baseSpeedMs: Number(points[0]?.properties?.basespeed),
          })
        : undefined;

      try {
        const saved = await savePlannedRoute({
          accessContext,
          setAccessContext,
          vesselId,
          voyageId: existingVoyageId ?? undefined,
          newVoyage,
          name: routeName,
          baseSpeed,
          waypoints: waypointPayload,
          jsonRequest,
          summary,
          optimisedFor,
        });
        const routeId = Number(saved?.route?.id);
        const voyageId = Number(saved?.voyage?.id);
        if (!Number.isFinite(routeId) || !Number.isFinite(voyageId)) {
          throw new Error("Save returned no route or voyage id.");
        }
        if (isNewVoyage) {
          // mirror the DB demotion so a stale active sibling greys out at once
          archiveVoyageSiblings(vesselId, voyageId);
          onVoyageCreated?.(saved.voyage);
        }

        // Clear the draft from the route store after a successful save.
        replaceRoute(routeStoreId, createDraftDiscardState(routeStoreId));
        // Swap the draft for the saved route id in the tree reducer.
        const selectionId = draftSelectionId(routeStoreId);
        if ((treeSelectedRouteIds ?? []).includes(selectionId)) {
          treeDispatch?.(
            treeSelectionActions.toggleRouteSelected(
              vesselId,
              voyageId,
              selectionId,
            ),
          );
        }
        treeDispatch?.(
          treeSelectionActions.toggleRouteSelected(vesselId, voyageId, routeId),
        );

        onPersistedRouteSaved?.({
          routeId,
          vesselId,
          vesselName: draft?.draftContext?.vesselName ?? "",
          voyageId,
          voyageName: saved.voyage?.name || draft?.draftContext?.voyageName || "",
          source: "planner",
          archivedVoyageName:
            saved.archivedVoyageId == null
              ? null
              : (storeStateRef.current?.entities?.voyagesById?.[saved.archivedVoyageId]?.name ?? ""),
          routeName,
          routeType: "planned",
          state: isNewVoyage ? "active" : "suggested",
          voyageActivated: isNewVoyage,
          timestamp: new Date().toISOString(),
          summary: summary ?? null,
          optimisedFor: optimisedFor ?? null,
        });

      } catch (err) {
        console.error("Failed to save suggested route:", err);
        addNotification?.({
          message: `Save failed: ${err?.message ?? "Unknown error"}`,
          type: "error",
        });
      }
    },
    [
      plannerDraftRoutes,
      accessContext,
      setAccessContext,
      onPersistedRouteSaved,
      onVoyageCreated,
      replaceRoute,
      archiveVoyageSiblings,
      treeSelectedRouteIds,
      treeDispatch,
      addNotification,
    ],
  );

  return {
    draftRoutes,
    treeDraftRoutes,
    mapDraftRoutes,
    pathfinderMapRoutes,
    selectedDraftRouteIds,
    toggleDraftSelected,
    handleDiscardDraft,
    discardDraftsForVoyage,
    handleRenameDraft,
    handleSaveDraft,
  };
}
